import { useEffect, useMemo, useState } from 'react'
import type { Taxonomy } from '../lib/types'

/**
 * Two-column category picker (requirement 9, design SS8.1).
 *
 * Mains on the left, the selected main's subs on the right -- the shape in
 * `requirements/mvp/example/category.png`. A sub is optional: the API accepts a
 * main on its own, so "食品酒水" with no sub is a complete answer and the picker
 * must not force a second tap.
 */
export function CategoryPicker({
  taxonomy,
  categoryId,
  subCategoryId,
  onChange,
}: {
  taxonomy: Taxonomy
  categoryId?: string
  subCategoryId?: string
  onChange: (next: { categoryId: string; subCategoryId?: string }) => void
}) {
  const [activeMain, setActiveMain] = useState(categoryId ?? taxonomy[0]?.id ?? 'other')

  // Follow an externally-driven change (an import row being selected, say)
  // without stomping on the user's own browsing of a different main.
  useEffect(() => {
    if (categoryId) setActiveMain(categoryId)
  }, [categoryId])

  const main = useMemo(
    () => taxonomy.find((m) => m.id === activeMain) ?? taxonomy[0],
    [taxonomy, activeMain],
  )

  return (
    <div className="grid grid-cols-[9rem_1fr] overflow-hidden rounded-2xl ring-1 ring-ink-line/60">
      <ul className="max-h-72 overflow-y-auto bg-ink" role="tablist" aria-label="主分類">
        {taxonomy.map((m) => {
          const selected = m.id === activeMain
          return (
            <li key={m.id}>
              <button
                type="button"
                role="tab"
                aria-selected={selected}
                onClick={() => {
                  setActiveMain(m.id)
                  // Picking a main is already a valid choice, and it clears any
                  // sub from the previous main -- which would otherwise be kept
                  // and submitted under a parent it does not belong to.
                  onChange({ categoryId: m.id })
                }}
                className={`flex w-full items-center gap-2 px-3 py-3 text-left text-sm ${
                  selected ? 'bg-ink-soft text-paper' : 'text-muted'
                }`}
              >
                <span
                  aria-hidden
                  className="size-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: m.color }}
                />
                <span className="truncate">{m.label}</span>
              </button>
            </li>
          )
        })}
      </ul>

      <div className="max-h-72 overflow-y-auto bg-ink-soft/50 p-2">
        {main ? (
          <div className="flex flex-wrap gap-2">
            <Chip
              label={`${main.label}（不分子類）`}
              selected={categoryId === main.id && !subCategoryId}
              onClick={() => onChange({ categoryId: main.id })}
            />
            {main.subs.map((sub) => (
              <Chip
                key={sub.id}
                label={sub.label}
                selected={categoryId === main.id && subCategoryId === sub.id}
                onClick={() => onChange({ categoryId: main.id, subCategoryId: sub.id })}
              />
            ))}
          </div>
        ) : null}
      </div>
    </div>
  )
}

function Chip({
  label,
  selected,
  onClick,
}: {
  label: string
  selected: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      className={`rounded-full px-3 py-2 text-sm ${
        selected ? 'bg-accent font-medium text-ink' : 'bg-ink text-muted ring-1 ring-ink-line'
      }`}
    >
      {label}
    </button>
  )
}

/** The label for a stored pair of ids, used wherever a category is displayed. */
export function categoryLabel(
  taxonomy: Taxonomy | undefined,
  categoryId: string,
  subCategoryId?: string,
): string {
  const main = taxonomy?.find((m) => m.id === categoryId)
  if (!main) return categoryId
  if (!subCategoryId) return main.label
  const sub = main.subs.find((s) => s.id === subCategoryId)
  return sub ? `${main.label}·${sub.label}` : main.label
}

export function categoryColor(taxonomy: Taxonomy | undefined, categoryId: string): string {
  return taxonomy?.find((m) => m.id === categoryId)?.color ?? '#94A3B8'
}
